import { ISortResult } from "services/shared/common";
import { DataPoint } from "../../components/maps/intensityMaps/shared/types";
import { RequestInitWithRetry, request } from "../../lib/request";

export interface IZeroSignalLevelParams {
  groups?: string[],
  homeLocations?: string[],
  deviceId?: string,
  zoneId?: string,
  boundingLatLong: string,
  geotilePrecision: number,
  from: number,
  to: number,
  operatorName?: string
}

export interface IZeroSignalDataPoint {
  geotile?: string,
  totalCount: number | null,
  zeroSignalCount: number | null,
  zeroSignalPercentage: number | null
}

export type ZeroSignalLevelApiResponse = IZeroSignalDataPoint[];

export type ZeroSignalLevelResponse = Array<DataPoint & { totalCount: number, zeroSignalCount: number }>;

export function getZeroSignalLevelIntensity(params: IZeroSignalLevelParams) {
  return async (options: RequestInitWithRetry): Promise<ZeroSignalLevelResponse> => {

    const input = {
      path: `/api/core/maps/zeroSignalLevelIntensity`,
      body: {
        ...params,
      }
    };

    const output = await request<ZeroSignalLevelApiResponse>(input, options);

    return output.map(dataPoint => {
      const { zeroSignalPercentage: percentage, ...rest } = dataPoint;
      return { percentage, ...rest };
    });
  };
}

export interface ISignalLevelGeoBoundsParams {
  groups?: string[],
  homeLocations?: string[],
  deviceId?: string,
  zoneId?: string
}

export interface IGeoBounds {
  top: number,
  left: number,
  bottom: number,
  right: number
}

export type GeoBoundsResponse = {
  geoBounds: IGeoBounds
} | null;

export function getSignalLevelGeoBounds(params: ISignalLevelGeoBoundsParams) {
  return async (options: RequestInitWithRetry): Promise<GeoBoundsResponse> => {
    const input = {
      path: `/api/core/maps/signalLevelGeoBounds`,
      body: {
        ...params
      }
    };

    return request<GeoBoundsResponse>(input, options);
  };
}

export interface INoServiceParams {
  groups?: string[],
  homeLocations?: string[],
  deviceId?: string,
  zoneId?: string,
  boundingLatLong: string,
  geotilePrecision: number,
  from: number,
  to: number,
  operatorName?: string
}

export interface INoServiceDataPoint {
  geotile?: string,
  totalCount: number,
  noServiceCount: number | null,
  noServicePercentage: number | null
}

export type NoServiceApiResponse = INoServiceDataPoint[];

export type NoServiceResponse = Array<DataPoint & { totalCount: number, noServiceCount: number }>;

export function getNoServiceIntensity(params: INoServiceParams) {
  return async (options: RequestInitWithRetry): Promise<NoServiceResponse> => {
    const input = {
      path: `/api/core/maps/noServiceIntensity`,
      body: {
        ...params
      }
    };

    const output = await request<NoServiceApiResponse>(input, options);

    return output.map(dataPoint => {
      const { noServicePercentage: percentage, ...rest } = dataPoint;
      return { percentage, ...rest };
    });
  };
}

export interface ICoverageParams {
  boundingLatLong: string,
  geotilePrecision: number,
  from: number,
  to: number,
  groups?: string[],
  homeLocations?: string[],
  deviceId?: string,
  zoneId?: string,
  operatorName?: string,
  bearerType: string
}

export interface ICoverageDataPoint {
  geotile?: string,
  totalCount: number,
  coverageCount: number | null,
  coveragePercentage: number | null
}

export type CoverageApiResponse = ICoverageDataPoint[];

export type CoverageResponse = Array<DataPoint & { totalCount: number, coverageCount: number }>;

export function getCoverageIntensity(params: ICoverageParams) {
  return async (options: RequestInitWithRetry): Promise<CoverageResponse> => {
    const input = {
      path: `/api/core/maps/coverageIntensity`,
      body: {
        ...params
      }
    };

    const output = await request<CoverageApiResponse>(input, options);

    return output.map(dataPoint => {
      const { coveragePercentage: percentage, ...rest } = dataPoint;
      return { percentage, ...rest };
    });
  };
}

export interface ISimProvidersParams {
  deviceId?: string,
  from: number,
  to: number
}

export type SimProvidersResponse = string[];

export function getSimProviders(params: ISimProvidersParams) {
  return async (options: RequestInitWithRetry): Promise<SimProvidersResponse> => {
    const input = {
      path: `/api/core/maps/simProviders`,
      body: {
        ...params,
      }
    };

    const output = await request<SimProvidersResponse>(input, options);

    return output;
  };
}

export interface IDeviceGeoBoundsParams {
  groups?: string[],
  homeLocations?: string[],
  zoneId?: string
}

export function getDeviceGeoBounds(params: IDeviceGeoBoundsParams) {
  return async (options: RequestInitWithRetry): Promise<GeoBoundsResponse> => {
    const input = {
      path: `/api/core/maps/deviceGeoBounds`,
      body: {
        ...params
      }
    };

    return request<GeoBoundsResponse>(input, options);
  };
}

export interface IDeviceStatsParams {
  groups?: string[],
  homeLocations?: string[],
  zoneId?: string,
  alertValues?: string[],
  alertField?: string,
  from?: number,
  to?: number
}

export type DeviceStatsResponse = {
  totalCount: number,
  withLocationCount: number
};

export function getDeviceStats(params: IDeviceStatsParams) {
  return async (options: RequestInitWithRetry): Promise<DeviceStatsResponse> => {
    const input = {
      path: `/api/core/maps/deviceStats`,
      body: { ...params }
    };

    const output = await request<DeviceStatsResponse>(input, options);

    return output;
  };
}


export interface IDeviceClusterParams {
  groups?: string[],
  homeLocations?: string[],
  zoneId?: string,
  boundingLatLong: string,
  geotilePrecision: number,
  alertValues?: string[],
  alertField?: string,
  from?: number,
  to?: number
}

export interface IDeviceCluster {
  keys: string[],
  lat: number,
  lon: number,
  count: number,
  visited: boolean
}

export type DeviceClusterResponse = {
  clusters: IDeviceCluster[],
  precision: number
};

export function getDeviceCluster(params: IDeviceClusterParams) {
  return async (options: RequestInitWithRetry): Promise<DeviceClusterResponse> => {
    const input = {
      path: `/api/core/maps/deviceCluster`,
      body: { ...params }
    };

    const output = {
      clusters: await request<IDeviceCluster[]>(input, options),
      precision: params.geotilePrecision
    };

    return output;
  };
}

export enum ALERT {
  red = 'red',
  yellow = 'yellow',
  green = 'green'
}

export const alertStatuses = [
  'averageDischargeStatus',
  'deviceLocationAccessPointStatus',
  'deviceLocationDistanceStatus',
  'deviceLocationTimeStatus',
  'deviceTimeErrorStatus',
  'deviceUserExperienceDropStatus',
  'deviceUserExperienceLowPowerStatus',
  'deviceUserExperienceRebootStatus',
  'deviceUtilisationIdleStatus',
  'deviceUtilisationOutOfContactStatus'
] as const;

export type AlertStatus = typeof alertStatuses[number];

export type AlertField = AlertStatus | 'deviceStatus';

export interface IClusterDevicesListParams {
  limit: number,
  offset: number,
  sort?: ISortResult,
  boundingLatLong: string[],
  groups?: string[],
  homeLocations?: string[],
  zoneId?: string,
  alertValues?: ALERT[],
  alertField?: AlertField,
  from?: number,
  to?: number
}

export interface IAlertData {
  updated: string,
  deviceStatus: string,
  deviceLocationStatus: string,
  deviceLocationDistanceStatus: string,
  deviceLocationTimeStatus: string,
  deviceLocationAccessPointStatus: string,
  deviceUtilisationStatus: string,
  deviceUtilisationIdleStatus: string,
  deviceUtilisationOutOfContactStatus: string,
  deviceUserExperienceStatus: string,
  deviceUserExperienceRebootStatus: string,
  deviceUserExperienceLowPowerStatus: string,
  deviceUserExperienceDropStatus: string,
  deviceTimeErrorStatus: string,
  averageDischargeStatus: string,
  averageDischarge: number,
  accessPoint: {
    ssid: string,
    bssid: string,
    updated: number,
    homeLocationId: string,
    homeLocation: {
      name: string
    }
  },
  deviceEventCounts: {
    rebootCount: number,
    lowPowerCount: number,
    dropCount: number
  },
  homeLocation: {
    id: string,
    name: string,
    distanceYellow: number,
    distanceRed: number
  }
}

export type ClusterDevicesListItem = {
  id: string,
  serialNumber: string,
  assetTag: string,
  imei: string,
  deviceName: string
} & IAlertData;

export type ClusterDevicesListResponse = {
  list: ClusterDevicesListItem[],
  count: number
};

export function getClusterDevicesList({ limit, offset, ...params }: IClusterDevicesListParams) {
  return async (options: RequestInitWithRetry): Promise<ClusterDevicesListResponse> => {

    const input = {
      path: `/api/core/maps/deviceCluster/devicesList`,
      qs: { limit, offset },
      body: { ...params },
    };

    return await request<ClusterDevicesListResponse>(input, options);
  };
}

export function exportClusterDevicesListAction(options: RequestInitWithRetry) {
  return async ({ limit, offset, ...rest }: IClusterDevicesListParams) => {
    const input = {
      path: `/api/core/maps/deviceCluster/exportDevicesList`,
      qs: { limit, offset },
      body: { ...rest },
    };

    return request(input, options);
  };
}
